import { Link } from "react-router-dom";
import { Mail, MapPin } from "lucide-react";

const footerLinks = {
  research: [
    { name: "Research Portfolio", path: "/research" },
    { name: "Publications", path: "/publications" },
    { name: "Videos", path: "/publications#videos" },
    { name: "Impact", path: "/impact" },
  ],
  about: [
    { name: "About the Centre", path: "/about" },
    { name: "Network", path: "/network" },
    { name: "Opportunities", path: "/opportunities" },
    { name: "Contact Us", path: "/contact" },
  ],
  resources: [
    { name: "Privacy Policy", path: "/privacy" },
    { name: "Terms of Use", path: "/terms" },
    { name: "Accessibility", path: "/accessibility" },
  ],
};

export const Footer = () => {
  const currentYear = new Date().getFullYear();

  return (
    <footer className="bg-slate-900 text-white" data-testid="site-footer">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Main Footer Content */}
        <div className="py-12 lg:py-16 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-12">
          {/* Brand Column */}
          <div className="lg:col-span-1">
            <Link to="/" className="flex items-center gap-3 mb-6" data-testid="footer-logo">
              <img 
                src="/logo-white.png" 
                alt="CCSA Logo" 
                className="w-10 h-10 rounded-lg object-contain bg-white"
              />
              <div>
                <span className="font-serif font-semibold text-white text-sm leading-tight block">
                  The Centre for Consciousness
                </span>
                <span className="font-serif font-semibold text-white text-sm leading-tight">
                  Studies Australia
                </span>
              </div>
            </Link>
            <p className="text-slate-400 text-sm leading-relaxed mb-6">
              Centre for Consciousness Studies Australia — advancing rigorous interdisciplinary research at the intersection of consciousness, AI, neurotechnology, and fundamental physics.
            </p>
          </div>

          {/* Research Links */}
          <div>
            <h3 className="font-serif font-semibold text-white mb-4">Research</h3>
            <ul className="space-y-3">
              {footerLinks.research.map((link) => (
                <li key={link.path}>
                  <Link
                    to={link.path}
                    className="text-slate-400 hover:text-white text-sm transition-colors"
                    data-testid={`footer-link-${link.name.toLowerCase().replace(/\s+/g, '-')}`}
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* About Links */}
          <div>
            <h3 className="font-serif font-semibold text-white mb-4">About</h3>
            <ul className="space-y-3">
              {footerLinks.about.map((link) => (
                <li key={link.path}>
                  <Link
                    to={link.path}
                    className="text-slate-400 hover:text-white text-sm transition-colors"
                    data-testid={`footer-link-${link.name.toLowerCase().replace(/\s+/g, '-')}`}
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h3 className="font-serif font-semibold text-white mb-4">Contact</h3>
            <ul className="space-y-4">
              <li className="flex items-start gap-3">
                <MapPin className="w-5 h-5 text-slate-400 flex-shrink-0 mt-0.5" />
                <span className="text-slate-400 text-sm">
                  Australia
                </span>
              </li>
              <li className="flex items-start gap-3">
                <Mail className="w-5 h-5 text-slate-400 flex-shrink-0 mt-0.5" />
                <a
                  href="mailto:info@mindcausation.com"
                  className="text-slate-400 hover:text-white text-sm transition-colors"
                  data-testid="footer-email"
                >
                  info@mindcausation.com
                </a>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="py-6 border-t border-slate-800">
          <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
            <p className="text-slate-400 text-sm">
              © {currentYear} Centre for Consciousness Studies Australia. All rights reserved.
            </p>
            <div className="flex items-center gap-6">
              {footerLinks.resources.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  className="text-slate-400 hover:text-white text-sm transition-colors"
                  data-testid={`footer-link-${link.name.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  {link.name}
                </Link>
              ))}
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
